import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { CreateBadgeRequest, UpdateBadgeRequest } from '../../../../../api/badge/badge.types';
import { Badge } from '../../../../../types/entity.types';

import { fileToBase64 } from '../../../../../utils/util';

import Button from '../../../../common/button/Button';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import TextInput from '../../../../common/input/text-input/TextInput';
import Modal from '../../../../common/modal/Modal';

import { ReactComponent as FileIcon } from '../../../../../resources/icons/file.svg';
import { ReactComponent as UploadIcon } from '../../../../../resources/icons/upload.svg';

import './manage-badge-modal.scss';

type Props = {
  badge?: Badge;
  createBadge: (badge: CreateBadgeRequest, onSuccess: () => void) => void;
  updateBadge: (badge: UpdateBadgeRequest, onSuccess: () => void) => void;
  deleteBadge: (externalReference: string, onSuccess: () => void) => void;
  close: () => void;
};

const ManageBadgeModal = ({ badge, createBadge, updateBadge, deleteBadge, close }: Props) => {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<File>();

  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!badge) return;

    setName(badge.badgeName);
  }, [badge]);

  const handleFileChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.files) return;
    setIcon(target.files[0]);
  };

  const iconName = useMemo(() => {
    if (icon && 'name' in icon) return icon.name;

    if (badge) return badge.iconName;

    return '';
  }, [icon, badge]);

  const handleSaveClick = async () => {
    let iconBase64: string | undefined;
    if (icon) {
      iconBase64 = await fileToBase64(icon);
    }

    if (badge) {
      const request: UpdateBadgeRequest = {
        badgeExternalReference: badge.externalReference,
        badgeName: name,
        iconName: iconName,
        iconExternalReference: iconBase64 ? undefined : badge.iconExternalReference,
        iconBase64
      };

      updateBadge(request, close);
      return;
    }

    const request: CreateBadgeRequest = {
      badgeName: name,
      iconName: iconName,
      iconBase64: iconBase64!
    };

    createBadge(request, close);
  };

  return (
    <Modal show>
      <div className="manage-badge-modal">
        <div className="manage-badge-modal__header">{badge ? 'Edit' : 'Add'} Badge</div>
        <div className="manage-badge-modal__content">
          <TextInput label="Badge Name:" name="name" value={name} onChange={({ target }) => setName(target.value)} />

          <div className="manage-badge-modal__file-input-container">
            <label>Upload Icon:</label>
            <FilePicker
              triggerElement={
                <div className="manage-badge-modal__file-input">
                  <FileIcon />
                  <input type="text" value={iconName} placeholder="Click button to upload" disabled />
                  <div className="manage-badge-modal__file-input__icon" onClick={() => fileRef.current?.click()}>
                    <UploadIcon />
                  </div>
                </div>
              }
              fileRef={fileRef}
              onChange={handleFileChange}
            />
          </div>
        </div>
        <div className="manage-badge-modal__button-container">
          <Button text="Cancel" onClick={close} />
          {badge && <Button text="Delete" onClick={() => deleteBadge(badge.externalReference, close)} />}
          <Button text="Save" onClick={handleSaveClick} disabled={!name || (!icon && !badge)} />
        </div>
      </div>
    </Modal>
  );
};

export default ManageBadgeModal;
